import type { ErrorEvent } from '@sentry/nextjs';

/**
 * Browser-only `beforeSend`. Kept separate from `before-send.ts` on purpose:
 * that one is scoped to the Node and Edge runtimes and its patterns
 * (ExperimentalWarning, csp-report, invalid_grant) are all server concerns.
 * This one filters things that can only happen inside somebody else's browser.
 */

/**
 * Meta's in-app browsers (Facebook, Instagram) inject a navigation-timing
 * beacon into every page they open, and it reports its own failures through
 * OUR `window.onerror` — so Sentry bills them to us. On unload it calls
 * `sendDataToNative`, which posts into the host app's native bridge; when that
 * bridge is mid-teardown the call throws, and the throw escapes to the page.
 * Four issues, all on /signup (it is the Meta-ad landing page), all zero users
 * impacted because it fires as the user is already leaving:
 *
 *   MUCKA-WEB-4G  Android  "Error invoking postMessage: Java exception was raised…"
 *   MUCKA-WEB-2W  Android  "Error invoking postMessage: Java object is gone"
 *   MUCKA-WEB-2F  iOS      "undefined is not an object (evaluating 'window.webkit.messageHandlers')"
 *
 * None of it is our code — `sendDataToNative`, `sendPageHideMessage` and
 * `window.webkit.messageHandlers` appear nowhere in this repo.
 *
 * 🚨 Archiving these one at a time does not work, which is why this exists.
 * The host app's error string is part of Sentry's grouping key, so every new
 * Java/JS failure mode from the same beacon mints a NEW issue: 2W was archived
 * forever in July and 4G — the same script, the same line, a different
 * exception text — arrived unresolved four weeks later.
 *
 * Matched on the STACK, not the message. Two reasons:
 *   - The messages are unbounded (see above) and the Android one is generated
 *     by Android's own `@JavascriptInterface` layer, so a blanket
 *     "Error invoking postMessage" ignore would ALSO swallow a genuine failure
 *     of Capacitor's `androidBridge.postMessage` in our own native app. That
 *     is a bug we would want to see.
 *   - `denyUrls` can't do it either. It filters on frame filename, and only
 *     the Android beacon has one (`app://navigation_performance_logger_android`);
 *     the iOS build is attributed to `app:///signup`, indistinguishable from
 *     our own page.
 * The frame names below are the one discriminator common to all of them.
 */
const META_BEACON_FILENAME = /navigation_performance_logger/i;

const META_BEACON_FUNCTIONS = [
  'sendDataToNative',
  'sendPageHideMessage',
  'sendBeforeUnloadMessage',
  '_handleBrowserPreparingToClose',
];

function isMetaInAppBrowserBeacon(event: ErrorEvent): boolean {
  const frames =
    event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];

  return frames.some((frame) => {
    if (frame.filename && META_BEACON_FILENAME.test(frame.filename)) return true;
    const fn = frame.function;
    // Endswith covers the `window._handleBrowserPreparingToClose` form.
    return !!fn && META_BEACON_FUNCTIONS.some((name) => fn === name || fn.endsWith(`.${name}`));
  });
}

/**
 * Something in the reader's browser reading our JSON-LD and falling over.
 *
 * The help site publishes schema.org structured data on every article
 * (`src/lib/help/jsonld.ts` → `<script type="application/ld+json">`). That
 * script tag is DATA — the browser never executes it — but SEO extensions,
 * scrapers and reader-mode tools parse it, and when one of them assumes a key
 * is present it throws into our `window.onerror`. Sentry then bills it to us:
 *
 *   MUCKA-WEB-6K  /integrations/connect-quickbooks
 *   MUCKA-WEB-6J  /schedule/colour-code-your-team-in-the-diary
 *   both: Safari, 0 users, "undefined is not an object (evaluating
 *   'r[\"@context\"].toLowerCase')", one frame, `global code`.
 *
 * 🚨 Matched on the MESSAGE here, which is the opposite of the beacon above,
 * and for the opposite reason. There the stack was the stable part and the text
 * varied; here the stack is useless — Safari attributes the frame to the
 * document itself (`app:///<our path>`, `global code`), indistinguishable from
 * a genuine inline-script failure of our own — while the JSON-LD key is the one
 * thing every variant of this must contain.
 *
 * It is safe to match on because **nothing in this repo ever reads a JSON-LD
 * key at runtime.** Every `@context` / `@type` in the codebase is a literal
 * being WRITTEN in `src/lib/help/jsonld.ts`; `JsonLd.tsx` serialises and stops.
 * So an error about accessing one cannot be ours. `jsonld-is-write-only.test.ts`
 * asserts that, because it is the assumption the whole filter rests on — the
 * day somebody adds a client-side parser, this stops being noise.
 *
 * Covers the sibling keys as well as `@context`. The browsers word the same
 * fault differently (Safari "undefined is not an object", Chrome "Cannot read
 * properties of undefined"), so pinning the exact sentence would mint a new
 * issue per browser — the lesson from 2W/4G, applied to the half that varies.
 */
const JSON_LD_KEY_ACCESS = /\[\s*["'@]{0,2}@(?:context|type|graph|id)["']?\s*\]/i;

function isJsonLdScraper(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(
    (value) => !!value.value && JSON_LD_KEY_ACCESS.test(value.value),
  );
}

/**
 * Vercel's preview/feedback toolbar throwing into our window.
 *
 * Vercel injects `/_next-live/feedback/instrument.<hash>.js` (served from
 * vercel.live) into demo and preview deployments. When its frame is detached
 * it throws through our listeners, and Sentry bills it to us:
 *
 *   MUCKA-WEB-DH  /schedule         "reading 'removeEventListener'" — its reply
 *                 handler calls `window.parent.removeEventListener`, and
 *                 `window.parent` is null in a detached frame
 *   MUCKA-WEB-DJ  /revenue-reports  "reading 'getItem'" on pagehide
 *
 * Both from one demo session, one replay, a new issue per message — so this
 * matches the stack, as the Meta beacon does. The other frame on both,
 * `_next/static/.../27iptmx6e3nfm.js` `r`, is Sentry's own `wrap()`.
 *
 * 🚨 Only the frame that THREW decides, never "any frame". The toolbar is an
 * instrumentation script, so it can sit lower in the stack of a genuine Mucka
 * fault; matching anywhere would hide our own errors on demo. Sentry stores
 * frames outermost-first, so the thrower is the last one with a filename.
 */
const VERCEL_TOOLBAR_FILENAME = /\/_next-live\//;

function isVercelToolbarThrow(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some((value) => {
    const frames = value.stacktrace?.frames ?? [];
    const thrower = [...frames].reverse().find((frame) => frame.filename);
    return !!thrower?.filename && VERCEL_TOOLBAR_FILENAME.test(thrower.filename);
  });
}

/**
 * React's production stand-in for a server render that threw.
 *
 * The real message is withheld on purpose ("omitted in production builds to
 * avoid leaking sensitive details"), so EVERY server-render failure in the app
 * reaches the browser wearing the same sentence — and Sentry, which groups on
 * that sentence and a stackless event, files them all as one issue.
 *
 * 🚨 This is the confirmed over-grouping the warning in `before-send.ts`
 * demands before anyone reaches for a fingerprint, and it was confirmed the way
 * that one says to — in Sentry, by evidence, not by theory. MUCKA-WEB-4R's four
 * occurrences are FOUR DIFFERENT BUGS on three routes, each identified by
 * pairing it with the server-side throw sharing its `trace_id`:
 *
 *   11 Sep  /quotes      MUCKA-WEB-81  the quote revision guard refusing a list read
 *   27 Aug  /schedule    MUCKA-WEB-34  column jobs.no_invoice_needed does not exist
 *   22 Aug  /onboarding  MUCKA-WEB-4S  resumeStepFromLegacyA() called from the server
 *   22 Aug  /onboarding  (no paired throw captured)
 *
 * Two of those are the incidents behind CLAUDE.md anti-patterns #18 and #28.
 * Filed together they read as one flaky page, and the issue's culprit shows
 * whichever route failed last — it said `/quotes` while three quarters of it
 * was somewhere else entirely.
 *
 * Keyed on `error_boundary`, NOT `boundary_route`. The boundary is the SEGMENT
 * that caught it ('quotes', 'jobs/[id]', 'authed'); the route is
 * `window.location.pathname`, so keying on that would put every job id in its
 * own issue and split the one number worth reading — how many pages are
 * affected — across all of them. Same lesson as an account id in a title.
 *
 * Left alone when the tag is missing: an untagged event groups as it always
 * did, which is the status quo rather than a worse guess.
 */
const SERVER_COMPONENTS_RENDER = /An error occurred in the Server Components render/;

function serverRenderFingerprint(event: ErrorEvent): string[] | null {
  const message = event.message || event.exception?.values?.[0]?.value || '';
  if (!SERVER_COMPONENTS_RENDER.test(message)) return null;

  const boundary = event.tags?.error_boundary;
  if (typeof boundary !== 'string' || !boundary) return null;

  return ['server-components-render', boundary];
}

/**
 * Mapbox's own session/billing call failing after we removed the map.
 *
 * MUCKA-WEB-DK, iOS app, /schedule: "this.errorCb is not a function … is null",
 * an unhandled rejection whose only frames are `mapbox-gl/src/util/sku_token.ts`
 * and `mapbox.ts`. mapbox-gl keeps its session check and map-load event on two
 * module-wide singletons; `map.remove()` sets their `errorCb` to null, and a
 * request still in flight that then fails calls it anyway. Our maps remove
 * themselves on unmount as they must (TodayMap, TodayMapFull, the engineer
 * map), so leaving Home for the diary while the session call is out, on a
 * connection that drops it, is enough. The map is gone before it throws, so
 * nobody sees anything, and nothing we write can close the window: the
 * singletons are not exported. Present in 3.27.0 and still in 3.33.0.
 *
 * Matched on the MESSAGE, for the JSON-LD reason: in the browser `beforeSend`
 * sees minified chunk URLs, not the `node_modules/mapbox-gl` paths Sentry
 * shows after symbolication. Safe because `errorCb` is Mapbox's name and
 * nothing in `src` uses it. `mapbox-errorcb-race.test.ts` runs Mapbox's real
 * class through the race and checks that premise, and goes red the day Mapbox
 * fixes it, which is when this should be deleted.
 */
const MAPBOX_REMOVED_MAP_CALLBACK = /\bthis\.errorCb is not a function\b/;

function isMapboxRemovedMapCallback(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(
    (value) => !!value.value && MAPBOX_REMOVED_MAP_CALLBACK.test(value.value),
  );
}

/**
 * Whether the inline white-screen watchdog replaced the page with "You're
 * offline" before this event, as a tag on EVERY event.
 *
 * MUCKA-WEB-AY: "Hydration failed … didn't match", native app only, mostly
 * Samsung WebViews, with React finding the wrong element rather than different
 * text. The leading theory is the watchdog: on a phone whose bundle has not
 * started 12s in, it empties <body> and shows the offline notice; if the bundle
 * then arrives, React hydrates against that notice and throws exactly this. The
 * hydration detector cannot confirm it — it stands down whenever the watchdog
 * wiped the page, by design — and the replays had expired. A tag on the error
 * itself answers it on the next event either way: `yes` on AY means the
 * watchdog, `no` rules it out.
 *
 * Answered, 2 Oct 2026: AY was `no` every time — it is the service worker
 * serving the offline page's HTML under another URL (see `MobileNav`'s
 * `offlineShell`). The watchdog's own hydration failure grouped separately as
 * MUCKA-WEB-B3, and that one was `yes`.
 */
function tagOfflineScreen(event: ErrorEvent): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { __muckaOfflineShown?: unknown; __muckaOfflineRestored?: unknown };
  // `restored`: the notice went up, then the bundle arrived and put the
  // server's page back before hydrating (the MUCKA-WEB-B3 fix). A hydration
  // error tagged `restored` means the restore did not do its job.
  const value = !w.__muckaOfflineShown ? 'no' : w.__muckaOfflineRestored ? 'restored' : 'yes';
  event.tags = { ...event.tags, offline_screen_shown: value };
}

export function sentryClientBeforeSend(event: ErrorEvent): ErrorEvent | null {
  if (isMapboxRemovedMapCallback(event)) return null;
  if (isMetaInAppBrowserBeacon(event)) return null;
  if (isJsonLdScraper(event)) return null;
  if (isVercelToolbarThrow(event)) return null;

  const serverRender = serverRenderFingerprint(event);
  if (serverRender) event.fingerprint = serverRender;

  tagOfflineScreen(event);

  return event;
}
